import { CianChatButtonWithAnimation } from '@cian/newbuilding-chat-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { onAnimationShown, onAnimationTooltipShown } from './analytics/analytics';
import { useChatAnimationCounter } from './hooks/useChatAnimationCounter';
import { getTooltipAnimationCookie, setTooltipAnimationCookie } from './utils/chatAnimationCounter';
import { isChatExistSelector, isLoadingSelector } from '../../selectors/consultantCianChat';
import { getFirstGaLabel } from '../../selectors/analytics';

export const CianChatButtonWithAnimationContainer = ({ onClick }: { onClick(): void }) => {
  const trackingCustomLabel = useSelector(getFirstGaLabel);
  const isLoading = useSelector(isLoadingSelector);
  const isChatExist = useSelector(isChatExistSelector);

  const { counter, incrementCounter, isFirstVisit } = useChatAnimationCounter();
  const [isButtonAnimationVisible, setIsButtonAnimationVisible] = React.useState(() => counter % 5 === 0);

  const [isTooltipAnimationVisible, setIsTooltipAnimationVisible] = React.useState(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();

    return !tooltipCookieExists && counter % 5 === 0 && !isChatExist;
  });

  React.useEffect(() => {
    setIsButtonAnimationVisible(isFirstVisit || counter % 5 === 0);
  }, [counter, isFirstVisit]);

  React.useEffect(() => {
    const tooltipCookieExists = getTooltipAnimationCookie();
    // istanbul ignore if
    if (tooltipCookieExists) {
      setIsTooltipAnimationVisible(false);
    } else {
      setIsTooltipAnimationVisible(isButtonAnimationVisible && !isChatExist);
    }
  }, [isButtonAnimationVisible, isChatExist]);

  // istanbul ignore next
  const handleAnimationStarted = React.useCallback(() => {
    onAnimationShown({ label: trackingCustomLabel });
    if (isTooltipAnimationVisible) {
      onAnimationTooltipShown({ label: trackingCustomLabel });
    }

    setTooltipAnimationCookie();
    incrementCounter();
  }, [isTooltipAnimationVisible, trackingCustomLabel, incrementCounter]);

  return (
    <CianChatButtonWithAnimation
      data-testid="ConsultantChatButton"
      isLoading={isLoading}
      isButtonAnimationVisible={isButtonAnimationVisible}
      isTooltipAnimationVisible={isTooltipAnimationVisible}
      onClick={onClick}
      animationDelayMs={30000}
      onAnimationStarted={handleAnimationStarted}
    />
  );
};
